// ✅ src/pages/VehicleV2/VehicleSalesorScrapReport.jsx
// Production listing template
// GET /api/vehicleSalesorScrapReport/branches
// GET /api/vehicleSalesorScrapReport/vehicleTypes
// GET /api/vehicleSalesorScrapReport/list

import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaArrowLeft,
  FaEye,
  FaFileExport,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, createSearchParams } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/vehicleSalesorScrapReport";

const toStr = (v) => String(v ?? "").trim();
const isSelect = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select";
};
const toNum = (v, d = 0) => {
  if (v === null || v === undefined || v === "") return d;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : d;
};
const formatAmount = (v) =>
  toNum(v, 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const pickSaleScrapId = (row) =>
  Number(
    row?.saleScrapId ??
      row?.saleorscrapId ??
      row?.SaleorScrapId ??
      row?.fld_saleorscrapid ??
      row?.fld_SaleorScrapId ??
      row?.id ??
      0
  ) || 0;

const exportToCsv = (rows, filename = "vehicle-sale-scrap-report.csv") => {
  if (!rows?.length) {
    toast.info("No data to export");
    return;
  }
  const safeRows = rows.map((r) => {
    const c = { ...r };
    delete c._row;
    return c;
  });
  const headers = Object.keys(safeRows[0]);
  const esc = (val) => {
    const s = String(val ?? "");
    if (s.includes('"') || s.includes(",") || s.includes("\n")) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const csv = [headers.join(","), ...safeRows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const VehicleSalesorScrapReport = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const [showFilters, setShowFilters] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [branches, setBranches] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const isBusy = loadingInit || loadingList;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId = localStorage.getItem("branchId") || "";
  const sessionBranchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, userId]
  );

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    branchId: sessionBranchType !== "HO" ? sessionBranchId : "Select",
    regNo: "",
    vehicleCode: "",
    vehicleName: "",
    status: "All",
    vehicleTypeId: "Select",
  });

  const handleChange = (e) => setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const branchOptions = useMemo(
    () =>
      (branches || [])
        .map((b) => ({
          value: toStr(b.branchId ?? b.fld_branchid ?? b.branchid ?? b.BranchId ?? b.id ?? b.value),
          label: toStr(b.branchName ?? b.fld_BranchName ?? b.fld_branchname ?? b.branchname ?? b.BranchName ?? b.label),
        }))
        .filter((o) => o.value || o.label),
    [branches]
  );

  const vehicleTypeOptions = useMemo(
    () =>
      (vehicleTypes || [])
        .map((v) => ({
          value: toStr(v.vehicleTypeId ?? v.fld_vehicletypeid ?? v.value ?? v.id),
          label: toStr(v.vehicleTypeName ?? v.fld_vehicletypeindex ?? v.fld_vehicletypename ?? v.VehicleTypeName ?? v.label),
        }))
        .filter((o) => o.label),
    [vehicleTypes]
  );

  const buildListParams = (f) => ({
    fromDate: toStr(f.fromDate),
    toDate: toStr(f.toDate),
    branchId: sessionBranchType !== "HO" ? sessionBranchId : isSelect(f.branchId) ? "Select" : toStr(f.branchId),
    regNo: toStr(f.regNo),
    vehicleCode: toStr(f.vehicleCode),
    vehicleName: toStr(f.vehicleName),
    status: toStr(f.status) || "All",
    vehicleTypeId: isSelect(f.vehicleTypeId) ? "Select" : toStr(f.vehicleTypeId),
  });

  const fetchInit = async () => {
    try {
      setLoadingInit(true);
      const [branchRes, vehicleTypeRes] = await Promise.all([
        axios.get(`${API_BASE}/branches`, {
          params: { branchType: sessionBranchType || "BRANCH", branchId: sessionBranchId || "" },
          headers: apiHeaders,
        }),
        axios.get(`${API_BASE}/vehicleTypes`, { headers: apiHeaders }),
      ]);
      if (!branchRes.data?.success) throw new Error(branchRes.data?.message || "Failed to load branches");
      if (!vehicleTypeRes.data?.success) throw new Error(vehicleTypeRes.data?.message || "Failed to load vehicle types");
      setBranches(branchRes.data.rows || []);
      setVehicleTypes(vehicleTypeRes.data.rows || []);
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to load filters");
    } finally {
      setLoadingInit(false);
    }
  };

  const loadList = async (nextFilters = filters, silent = false) => {
    try {
      setLoadingList(true);
      const res = await axios.get(`${API_BASE}/list`, {
        params: buildListParams(nextFilters),
        headers: apiHeaders,
      });
      if (!res.data?.success) throw new Error(res.data?.message || "List fetch failed");
      const fetchedRows = res.data.rows || [];
      setAllRows(fetchedRows);
      setCurrentPage(1);
      if (!silent) toast.success(`Found ${res.data?.count ?? fetchedRows.length} records`);
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to load report");
      setAllRows([]);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchInit();
  }, []);

  const handleSearch = () => loadList(filters);
  const handleViewAll = async () => {
    const reset = {
      fromDate: "",
      toDate: "",
      branchId: sessionBranchType !== "HO" ? sessionBranchId : "Select",
      regNo: "",
      vehicleCode: "",
      vehicleName: "",
      status: "All",
      vehicleTypeId: "Select",
    };
    setFilters(reset);
    await loadList(reset, true);
    toast.info("Showing all records");
  };
  const handleClearFilters = () => {
    setFilters({
      fromDate: "",
      toDate: "",
      branchId: sessionBranchType !== "HO" ? sessionBranchId : "Select",
      regNo: "",
      vehicleCode: "",
      vehicleName: "",
      status: "All",
      vehicleTypeId: "Select",
    });
    setAllRows([]);
    setCurrentPage(1);
    toast.info("Filters cleared!");
  };

  const handlePrintReport = () => {
    navigate({
      pathname: "/vehicle/vehicleSalesandScrapReport_Print",
      search: `?${createSearchParams({
        fromDate: filters.fromDate || "",
        toDate: filters.toDate || "",
        branchId: sessionBranchType !== "HO" ? sessionBranchId : filters.branchId || "Select",
        regNo: filters.regNo || "",
        vehicleCode: filters.vehicleCode || "",
        vehicleName: filters.vehicleName || "",
        status: filters.status || "All",
        vehicleTypeId: filters.vehicleTypeId || "Select",
      }).toString()}`,
    });
  };

  const handleDocuments = (row) => {
    const saleScrapId = pickSaleScrapId(row);
    if (!saleScrapId) {
      toast.info("Document id not available");
      return;
    }
    navigate(
      {
        pathname: "/vehicle/VehicleSalesandScrapReport_Doc",
        search: `?${createSearchParams({
          saleScrapId: String(saleScrapId),
          saleorscrapId: String(saleScrapId),
        }).toString()}`,
      },
      { state: { saleScrapId } }
    );
  };

  const total = allRows.length;
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return allRows.slice(start, start + itemsPerPage);
  }, [allRows, currentPage]);

  const fieldClass = `w-full max-w-full min-w-0 box-border px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
  }`;
  const labelClass = `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`;

  return (
    <VehicleLayoutWrapper>
      <div className={`w-full max-w-full min-w-0 overflow-x-hidden box-border min-h-screen p-4 md:p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-2xl md:text-3xl">VEHICLE SALE / SCRAP REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`w-full max-w-full min-w-0 overflow-hidden p-4 md:p-6 mt-6 rounded-2xl shadow-xl border box-border ${
          isDark ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white" : "bg-white text-black"
        }`}>
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="min-w-0">
              <h2 className={`text-xl md:text-2xl font-bold break-words ${isDark ? "text-white" : "text-gray-900"}`}>Vehicle Sale / Scrap Report</h2>
            </div>
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <button onClick={handlePrintReport} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white`}><FaPrint /> Print</button>
              <button onClick={() => exportToCsv(allRows)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}><FaFileExport /> Export</button>
              <button onClick={fetchInit} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaSyncAlt /> Refresh</button>
              <button onClick={() => navigate(-1)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}><FaArrowLeft /> Back</button>
              <button onClick={() => setShowFilters((s) => !s)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}>
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-4 md:p-6 rounded-lg mb-6 overflow-hidden ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>Search Criteria</h3>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={handleClearFilters} type="button" className={`px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"}`}>Clear</button>
                  <button onClick={handleViewAll} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaEye /> View All</button>
                  <button onClick={handleSearch} disabled={isBusy} type="button" className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaSearch /> {loadingList ? "Searching..." : "Search"}</button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="min-w-0">
                  <label className={labelClass}>From Date</label>
                  <input type="date" name="fromDate" value={filters.fromDate} onChange={handleChange} className={fieldClass} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>To Date</label>
                  <input type="date" name="toDate" value={filters.toDate} onChange={handleChange} className={fieldClass} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Branch</label>
                  <select name="branchId" value={filters.branchId} onChange={handleChange} className={fieldClass} disabled={sessionBranchType !== "HO"}>
                    <option value="Select">Select</option>
                    {branchOptions.map((b) => <option key={`${b.value}-${b.label}`} value={b.value}>{b.label || b.value}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select name="vehicleTypeId" value={filters.vehicleTypeId} onChange={handleChange} className={fieldClass}>
                    <option value="Select">Select</option>
                    {vehicleTypeOptions.map((v) => <option key={`${v.value}-${v.label}`} value={v.value}>{v.label}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Reg.No</label>
                  <input type="text" name="regNo" value={filters.regNo} onChange={handleChange} className={fieldClass} placeholder="Enter vehicle reg no" />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Code</label>
                  <input type="text" name="vehicleCode" value={filters.vehicleCode} onChange={handleChange} className={fieldClass} placeholder="Enter vehicle code" />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Name</label>
                  <input type="text" name="vehicleName" value={filters.vehicleName} onChange={handleChange} className={fieldClass} placeholder="Enter vehicle name" />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Sales Or Scrap</label>
                  <select name="status" value={filters.status} onChange={handleChange} className={fieldClass}>
                    <option value="All">All</option>
                    <option value="Sales">Sales</option>
                    <option value="scrap">scrap</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"}`}>Total Records: {total}</div>
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>{loadingInit ? "Loading dropdowns..." : loadingList ? "Searching..." : "Ready"}</div>
          </div>

          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg">
            {loadingInit || loadingList ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}><div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</div></div>
            ) : pagedRows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Records Found</div>
                <p className={`mt-2 text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>Adjust filters or click View All.</p>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">S.No</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Vehicle Code</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Vehicle Name</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Reg No</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Branch</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Status</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Sold Price</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Documents</th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {pagedRows.map((item, index) => (
                    <tr key={pickSaleScrapId(item) || `${item.vehicleCode}-${index}`} className={`${index % 2 === 0 ? (isDark ? "bg-gray-800" : "bg-white") : (isDark ? "bg-gray-750" : "bg-gray-50")} ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{item.vehicleCode ?? ""}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{item.name ?? ""}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{item.regNo ?? ""}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{item.branch ?? ""}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{item.status ?? ""}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{formatAmount(item.price ?? 0)}</td>
                      <td className="px-3 py-3 text-sm whitespace-nowrap">
                        <button type="button" onClick={() => handleDocuments(item)} className="text-blue-500 underline hover:text-blue-700">Documents</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {total > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
              <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, total)} of {total} entries
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""}`}>Prev</button>
                <div className={`px-4 py-2 border rounded-lg ${isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}>Page {currentPage} / {totalPages}</div>
                <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : ""}`}>Next</button>
              </div>
            </div>
          )}

          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleSalesorScrapReport;